import { useEffect, useState } from "react";
import { useRouter } from "expo-router";
import * as WebBrowser from "expo-web-browser";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";

import HipaaLogo from "@/assets/images/hipaa-logo.png";
import Soc2Logo from "@/assets/images/soc2-logo.png";
import AppleIcon from "@/assets/svg/apple.svg";
import GoogleColorIcon from "@/assets/svg/google_color.svg";
import { CheckYourEmail } from "@/blocks/CheckYourEmail";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnImage } from "@/components/VnImage";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import {
  useCheckAccountStatusMutation,
  useCheckUsernameAccountStatusMutation,
  useSendSignInLinkMutation,
} from "@/data/mutations/authMutations";
import {
  authIdentifierFormSchema,
  AuthIdentifierFormType,
} from "@/domain/auth";
import { useSSOAuth } from "@/hooks/useSSOAuth";
import { useTranslator } from "@/hooks/useTranslate";
import {
  isManualLogoutAtom,
  onboardingShortCodeDataAtom,
} from "@/stores/atoms";
import {
  openPrivacyPolicyUrl,
  openTermsAndConditionsUrl,
} from "@/utils/linking";

WebBrowser.maybeCompleteAuthSession();

const AuthScreen = () => {
  const router = useRouter();
  const { t } = useTranslator();

  const onboardingShortCodeData = useAtomValue(onboardingShortCodeDataAtom);
  const setIsManualLogout = useSetAtom(isManualLogoutAtom);

  // Clear the manual logout flag when this screen mounts so that any future
  // automatic sign-outs (session expiry etc.) redirect to onboarding, not /auth.
  useEffect(() => {
    setIsManualLogout(false);
  }, [setIsManualLogout]);

  const [hasPendingConfirmation, setHasPendingConfirmation] = useState(false);

  const patientAuthForm = useForm<AuthIdentifierFormType>({
    defaultValues: {
      identifier: "",
    },
    resolver: standardSchemaResolver(authIdentifierFormSchema),
  });

  const { mutate: sendSignInLink, isPending: isSendingSignInLink } =
    useSendSignInLinkMutation();
  const {
    mutateAsync: checkAccountStatus,
    isPending: isCheckingAccountStatus,
  } = useCheckAccountStatusMutation();
  const {
    mutate: checkUsernameAccountStatus,
    isPending: isCheckingUsernameAccountStatus,
  } = useCheckUsernameAccountStatusMutation();

  const {
    promptGoogleAuthAsync,
    isGoogleLoading,
    isGoogleAuthReady,
    promptAppleAuthAsync,
    isAppleLoading,
  } = useSSOAuth(onboardingShortCodeData?.code);

  const handleSubmit = async ({ identifier }: AuthIdentifierFormType) => {
    if (!identifier.includes("@")) {
      checkUsernameAccountStatus(identifier, {
        onSuccess: accountStatus => {
          if (!accountStatus) return;

          router.push({
            pathname: accountStatus.hasAccount
              ? "/password"
              : "/create-username-account",
            params: { username: identifier },
          });
        },
        onError: () => {
          patientAuthForm.setError("identifier", {
            message: t("common:validation.genericError"),
          });
        },
      });
      return;
    }

    const { hasAuth } = await checkAccountStatus(identifier);

    if (hasAuth) {
      router.push({ pathname: "/password", params: { email: identifier } });
      return;
    }

    sendSignInLink(
      {
        email: identifier,
        code: onboardingShortCodeData?.code ?? "",
      },
      {
        onSuccess: () => {
          setHasPendingConfirmation(true);
        },
      },
    );
  };

  return (
    <ScreenLayout className="py-12">
      <VnBox className="mt-safe-offset-10 web:mt-10 flex items-center px-2 sm:my-auto md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          {hasPendingConfirmation ? (
            <CheckYourEmail
              email={patientAuthForm.getValues("identifier")}
              onBackPress={() => {
                setHasPendingConfirmation(false);
              }}
            />
          ) : (
            <VnBox className="flex flex-col gap-6">
              <VnBox className="gap-2">
                <VnText
                  variant="headerOne"
                  i18nKey="native:authScreen.title"
                  className="text-center"
                />
                <VnText
                  variant="lg"
                  className="text-muted-foreground text-center"
                  i18nKey="native:authScreen.subtitle"
                />
              </VnBox>
              <VnForm form={patientAuthForm} isInputOnlyForm>
                <VnCard>
                  <VnTextInput
                    control={patientAuthForm.control}
                    name="identifier"
                    labelI18nKey="native:authScreen.identifierInput"
                    placeholder={t("native:authScreen.identifierInput")}
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="username"
                    autoComplete="username"
                    returnKeyType="done"
                    enablesReturnKeyAutomatically
                    onSubmitEditing={() => {
                      void patientAuthForm.handleSubmit(handleSubmit)();
                    }}
                  />
                  <VnButton
                    onPress={() => {
                      void patientAuthForm.handleSubmit(handleSubmit)();
                    }}
                    i18nKey="common:continue"
                    isLoading={
                      isSendingSignInLink ||
                      isCheckingAccountStatus ||
                      isCheckingUsernameAccountStatus
                    }
                    className="rounded-md"
                  />
                  <VnBox className="my-2 flex-row items-center gap-3">
                    <VnBox className="bg-border h-[1px] flex-1" />
                    <VnText
                      variant="sm"
                      i18nKey="common:or"
                      className="text-muted-foreground uppercase"
                    />
                    <VnBox className="bg-border h-[1px] flex-1" />
                  </VnBox>
                  <VnBox className="gap-3">
                    <VnButton
                      disabled={
                        isGoogleLoading || isAppleLoading || !isGoogleAuthReady
                      }
                      isLoading={isGoogleLoading}
                      onPress={() => {
                        void promptGoogleAuthAsync();
                      }}
                      i18nKey="native:authScreen.continueWithGoogle"
                      variant="secondary"
                      IconComponent={GoogleColorIcon}
                      className="rounded-md"
                    />
                    <VnButton
                      disabled={isGoogleLoading || isAppleLoading}
                      isLoading={isAppleLoading}
                      onPress={() => {
                        void promptAppleAuthAsync();
                      }}
                      i18nKey="native:authScreen.continueWithApple"
                      variant="secondary"
                      IconComponent={AppleIcon}
                      className="rounded-md"
                    />
                  </VnBox>
                </VnCard>
              </VnForm>
              <VnBox className="inline-flex w-full flex-row items-center justify-center gap-1">
                <VnText
                  i18nKey="native:authScreen.youAgree"
                  variant="smBold"
                  className="text-muted-foreground text-center"
                  i18nProps={{
                    components: {
                      underline: (
                        <VnText
                          variant="lgBold"
                          className="text-muted-foreground underline hover:cursor-pointer"
                          accessibilityRole="link"
                          onPress={() => {
                            void openTermsAndConditionsUrl();
                          }}
                        />
                      ),
                      privacyLink: (
                        <VnText
                          variant="lgBold"
                          className="text-muted-foreground underline hover:cursor-pointer"
                          accessibilityRole="link"
                          onPress={() => {
                            void openPrivacyPolicyUrl();
                          }}
                        />
                      ),
                    },
                  }}
                />
              </VnBox>
              <VnText
                i18nKey="native:authScreen.dataSecured"
                variant="smBold"
                className="mt-3 text-center"
              />
              <VnBox className="flex-row items-center justify-center gap-6">
                <VnImage
                  source={HipaaLogo}
                  className="h-12 w-12"
                  contentFit="contain"
                />
                <VnImage
                  source={Soc2Logo}
                  className="h-[3.2rem] w-[3.2rem]" // SOC2 logo has different aspect ratio, so we adjust the size to make it visually balanced with HIPAA logo
                  contentFit="contain"
                />
              </VnBox>
            </VnBox>
          )}
        </VnBox>
      </VnBox>
    </ScreenLayout>
  );
};

export { AuthScreen };
